import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { useState, lazy, Suspense } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Users, Star, Shield, Heart, CheckCircle, Calendar, Briefcase } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";

import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalButtonGroup } from "@/components/ui/BrutalButtonGroup";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { 
  BrutalSelect,
  BrutalSelectTrigger,
  BrutalSelectContent,
  BrutalSelectItem,
  BrutalSelectValue,
} from "@/components/ui/BrutalSelect";
import { Skeleton } from "@/components/ui/skeleton";
import { ProfessionalsCarousel } from "@/components/home/ProfessionalsCarousel";
import { CTABanner } from "@/components/home/CTABanner";
import { useAuth } from "@/contexts/AuthContext";
import {
  useFeaturedProfessionals, 
  useUpcomingEvents, 
  useHomeStats,
  useLatestJobs,
  useHomeRealtimeSubscriptions 
} from "@/hooks/useHomeData";
import { useBlogArticles } from "@/hooks/useBlogArticles";
import { useActiveCategories } from "@/hooks/useActiveCategories";
// Hero image served from public folder for better LCP (preloaded in index.html)
const heroImage = "/images/hero-2.png";

// Lazy load below-the-fold components
const EventsCarousel = lazy(() => import("@/components/home/EventsCarousel"));
const JobsCarousel = lazy(() => import("@/components/home/JobsCarousel"));
const BlogCarousel = lazy(() => import("@/components/home/BlogCarousel"));
const NewsletterForm = lazy(() => import("@/components/newsletter/NewsletterForm").then(m => ({ default: m.NewsletterForm })));
const BecomeProCTA = lazy(() => import("@/components/home/BecomeProCTA").then(m => ({ default: m.BecomeProCTA })));

// Skeleton for lazy loaded sections
const SectionSkeleton = ({ height = "h-96" }: { height?: string }) => (
  <div className={`${height} flex items-center justify-center`}>
    <Skeleton className={`w-full max-w-7xl mx-auto ${height}`} />
  </div>
);

// Default categories fallback
const DEFAULT_CATEGORIES = [
  { value: "psychologist", icon: "🧠" },
  { value: "doctor", icon: "⚕️" },
  { value: "lawyer", icon: "⚖️" },
  { value: "wedding_planner", icon: "💒" },
  { value: "photographer", icon: "📸" },
  { value: "personal_trainer", icon: "💪" }
];

const Index = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, screeningStatus } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState<'all' | 'professionals' | 'events' | 'jobs'>('all');

  // Use TanStack Query hooks with caching
  const { data: featuredProfessionals = [], isLoading: professionalsLoading } = useFeaturedProfessionals();
  const { data: upcomingEvents = [], isLoading: eventsLoading } = useUpcomingEvents();
  const { data: latestJobs = [], isLoading: jobsLoading } = useLatestJobs();
  const { data: blogArticles = [], isLoading: blogLoading } = useBlogArticles(6);
  const { data: stats = { professionals: 0, users: 0, reviews: 0, events: 0, companies: 0 } } = useHomeStats();
  
  // Get active categories dynamically (already sorted by count)
  // Normalize language to 2-char code (e.g., "it-IT" -> "it") with fallback
  const rawLang = (i18n.resolvedLanguage || i18n.language || 'it').substring(0, 2);
  const lang = (['it', 'en', 'es'].includes(rawLang) ? rawLang : 'it') as 'it' | 'en' | 'es';
  const { data: activeCategories = [], isLoading: categoriesLoading } = useActiveCategories(lang);
  
  // Take top 6 categories, fallback to defaults if none available
  const popularCategories = activeCategories.length > 0 
    ? activeCategories.slice(0, 6) 
    : DEFAULT_CATEGORIES.map(cat => ({
        value: cat.value,
        label: t(`home.categories.${cat.value}`),
        icon: cat.icon,
        count: 0
      }));

  // Setup realtime subscriptions that invalidate cache
  useHomeRealtimeSubscriptions();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const q = encodeURIComponent(searchQuery.trim());
      switch (searchType) {
        case 'all':
          navigate(`/search?q=${q}`);
          break;
        case 'professionals':
          navigate(`/professionals?q=${q}`);
          break;
        case 'events':
          navigate(`/events?search=${q}`);
          break;
        case 'jobs':
          navigate(`/jobs?search=${q}`);
          break;
      }
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t("home.heroTitle")}
        description={t("home.heroSubtitle")}
        type="website"
      />
      <Header />
      

      <main className="flex-1">
        {/* Hero Section - Single column */}
        <section className="bg-surface py-12 md:py-16 px-4 overflow-hidden">
          <div className="max-w-5xl mx-auto text-center">
            {/* Title */}
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold mb-6 text-dark">
              {t("home.heroTitle")}
            </h1>
            
            {/* Subtitle */}
            <p className="text-lg md:text-xl mb-8 text-muted-foreground max-w-3xl mx-auto">
              {t("home.heroSubtitle")}
            </p>
            
            {/* Hero Image - Full width with explicit dimensions to prevent layout shift */}
            <div className="w-screen relative left-1/2 -translate-x-1/2 pointer-events-none z-[1] mt-[-5%] sm:mt-[-6%] md:mt-[-8%] lg:mt-[-10%] xl:mt-[-12%] 2xl:mt-[-15%] mb-0 sm:mb-[-1%] md:mb-[-3%] lg:mb-[-5%] xl:mb-[-10%] 2xl:mb-[-15%]">
              <img 
                src={heroImage} 
                alt="Inclusia Community" 
                className="w-full"
                loading="eager"
                width={1920}
                height={1080}
                fetchPriority="high"
              />
            </div>
            
            {/* Search Bar */}
            <form onSubmit={handleSearch} className="relative z-[2] mb-8 max-w-3xl mx-auto">
              <div className="flex flex-row gap-3">
                {/* Input + Select uniti in un blocco unico */}
                <div className="flex flex-1 border-[3px] border-dark shadow-[4px_4px_0px_0px_hsl(var(--dark))]">
                  <BrutalInput
                    type="text"
                    placeholder={t("home.searchPlaceholder2")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1 text-lg border-0 shadow-none focus:ring-0 rounded-none"
                  />
                  <BrutalSelect value={searchType} onValueChange={(v) => setSearchType(v as typeof searchType)}>
                    <BrutalSelectTrigger className="w-[140px] sm:w-[180px] border-0 border-l-[3px] border-dark rounded-none shadow-none">
                      <BrutalSelectValue />
                    </BrutalSelectTrigger>
                    <BrutalSelectContent>
                      <BrutalSelectItem value="all">{t("home.searchTypeAll")}</BrutalSelectItem>
                      <BrutalSelectItem value="professionals">{t("home.searchTypeProfessionals")}</BrutalSelectItem>
                      <BrutalSelectItem value="events">{t("home.searchTypeEvents")}</BrutalSelectItem>
                      <BrutalSelectItem value="jobs">{t("home.searchTypeJobs")}</BrutalSelectItem>
                    </BrutalSelectContent>
                  </BrutalSelect>
                </div>
                {/* Bottone cerca separato */}
                <BrutalButton type="submit" className="px-[18px]" aria-label={t("common.search")}>
                  <Search className="w-5 h-5 md:w-6 md:h-6" />
                </BrutalButton>
              </div>
            </form>
            
            {/* CTA Buttons */}
            <div className="flex flex-col gap-4 items-center">
              <BrutalButtonGroup 
                items={[
                  { label: t("home.ctaFindProfessionals"), to: "/professionals", icon: Users },
                  { label: t("home.ctaFindEvents"), to: "/events", icon: Calendar },
                  { label: t("home.ctaFindJobs"), to: "/jobs", icon: Briefcase },
                ]}
              />
              {/* Hide "Become professional" button if user is already verified */}
              {(!isAuthenticated || screeningStatus !== 'approved') && (
                <Link to="/become-professional">
                  <BrutalButton size="lg" variant="outline" className="w-full sm:w-auto">
                    {t("home.ctaBecome")}
                  </BrutalButton>
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Professionals Carousel */}
        <ProfessionalsCarousel professionals={featuredProfessionals} isLoading={professionalsLoading} />

        {/* Popular Categories */}
        <section className="pt-4 pb-12 px-4">
          <div className="max-w-7xl mx-auto flex justify-center">
            <div className="flex flex-col lg:flex-row items-center gap-4">
              {/* Title */}
              <h2 className="font-heading text-2xl font-bold whitespace-nowrap">
                {t("home.popularCategories")}
              </h2>
              
              {/* Responsive grid on mobile/tablet, inline on desktop */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:inline-flex border-[3px] border-dark shadow-[4px_4px_0px_0px_hsl(var(--dark))] overflow-hidden w-full max-w-xs md:max-w-md lg:w-auto lg:max-w-none">
                {categoriesLoading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <div 
                      key={i} 
                      className={cn(
                        "h-10 bg-muted animate-pulse",
                        // Mobile: border-top except first
                        i > 0 && "border-t-[3px] border-dark md:border-t-0",
                        // Tablet: border-left on odd indices
                        i % 2 !== 0 && "md:border-l-[3px] md:border-dark lg:border-l-0",
                        // Tablet: border-top on rows 2+
                        i >= 2 && "md:border-t-[3px] md:border-dark lg:border-t-0",
                        // Desktop: border-left except first
                        i > 0 && "lg:border-l-[3px] lg:border-dark"
                      )}
                    />
                  ))
                ) : (
                  popularCategories.slice(0, 6).map((cat, index) => (
                    <Link
                      key={cat.value}
                      to={`/professionals?category=${cat.value}`}
                      className={cn(
                        "inline-flex items-center justify-center gap-2 px-4 py-2 bg-white text-dark text-sm font-semibold transition-colors",
                        "hover:bg-accent active:bg-accent/80",
                        // Mobile: border-top except first
                        index > 0 && "border-t-[3px] border-dark md:border-t-0",
                        // Tablet: border-left on odd indices (right column)
                        index % 2 !== 0 && "md:border-l-[3px] md:border-dark lg:border-l-0",
                        // Tablet: border-top on rows 2+ (index >= 2)
                        index >= 2 && "md:border-t-[3px] md:border-dark lg:border-t-0",
                        // Desktop: border-left except first
                        index > 0 && "lg:border-l-[3px] lg:border-dark"
                      )}
                    >
                      <span className="text-lg">{cat.icon}</span>
                      <span>{cat.label}</span>
                    </Link>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <CTABanner />

        {/* Events Carousel - Lazy loaded */}
        <Suspense fallback={<SectionSkeleton height="h-80" />}>
          <EventsCarousel events={upcomingEvents} isLoading={eventsLoading} />
        </Suspense>

        {/* Jobs Carousel - Lazy loaded */}
        <Suspense fallback={<SectionSkeleton height="h-80" />}>
          <JobsCarousel jobs={latestJobs} isLoading={jobsLoading} />
        </Suspense>

        {/* Blog Carousel - Lazy loaded */}
        <Suspense fallback={<SectionSkeleton height="h-80" />}>
          <BlogCarousel articles={blogArticles} isLoading={blogLoading} />
        </Suspense>

        {/* Stats */}
        <section className="py-16 px-4 bg-dark text-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-heading text-3xl font-bold mb-12 text-center">
              {t("home.statsTitle")}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stats.professionals}+
                </div>
                <div className="text-gray-300 text-sm md:text-base">{t("home.statsProfessionals")}</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stats.companies}+
                </div>
                <div className="text-gray-300 text-sm md:text-base">{t("home.statsCompanies")}</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stats.users}+
                </div>
                <div className="text-gray-300 text-sm md:text-base">{t("home.statsUsers")}</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stats.reviews}+
                </div>
                <div className="text-gray-300 text-sm md:text-base">{t("home.statsReviews")}</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-primary mb-2">
                  {stats.events}+
                </div>
                <div className="text-gray-300 text-sm md:text-base">{t("home.statsEvents")}</div>
              </div>
            </div>
          </div>
        </section>

        {/* Newsletter - Only show for non-authenticated users - Lazy loaded */}
        {!isAuthenticated && (
          <section className="py-16 px-4 bg-surface">
            <Suspense fallback={<SectionSkeleton height="h-40" />}>
              <NewsletterForm variant="compact" />
            </Suspense>
          </section>
        )}

        {/* How It Works - Only show for non-authenticated users */}
        {!isAuthenticated && (
          <section className="py-16 px-4">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-3xl font-bold mb-12 text-center">
                {t("home.howItWorksTitle")}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary border-[3px] border-dark shadow-[4px_4px_0px_#101322] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Search className="w-10 h-10 text-dark" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-3">
                    1. {t("home.step1Title")}
                  </h3>
                  <p className="text-muted-foreground">{t("home.step1Desc")}</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary border-[3px] border-dark shadow-[4px_4px_0px_#101322] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Users className="w-10 h-10 text-dark" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-3">
                    2. {t("home.step2Title")}
                  </h3>
                  <p className="text-muted-foreground">{t("home.step2Desc")}</p>
                </div>
                <div className="text-center">
                  <div className="w-20 h-20 bg-primary border-[3px] border-dark shadow-[4px_4px_0px_#101322] rounded-full flex items-center justify-center mx-auto mb-6">
                    <Star className="w-10 h-10 text-dark" />
                  </div>
                  <h3 className="font-heading text-xl font-bold mb-3">
                    3. {t("home.step3Title")}
                  </h3>
                  <p className="text-muted-foreground">{t("home.step3Desc")}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Why Choose Inclusia - Only show for non-authenticated users */}
        {!isAuthenticated && (
          <section className="py-16 px-4 bg-surface">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-3xl font-bold mb-12 text-center">
                {t("home.whyChooseTitle")}
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <BrutalCard>
                  <div className="text-center p-2">
                    <Shield className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-bold mb-3">
                      {t("home.feature1Title")}
                    </h3>
                    <p className="text-muted-foreground">{t("home.feature1Desc")}</p>
                  </div>
                </BrutalCard>
                <BrutalCard>
                  <div className="text-center p-2">
                    <Heart className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-bold mb-3">
                      {t("home.feature2Title")}
                    </h3>
                    <p className="text-muted-foreground">{t("home.feature2Desc")}</p>
                  </div>
                </BrutalCard>
                <BrutalCard>
                  <div className="text-center p-2">
                    <CheckCircle className="w-16 h-16 text-primary mx-auto mb-4" />
                    <h3 className="font-heading text-xl font-bold mb-3">
                      {t("home.feature3Title")}
                    </h3>
                    <p className="text-muted-foreground">{t("home.feature3Desc")}</p>
                  </div>
                </BrutalCard>
              </div>
            </div>
          </section>
        )}

        {/* Become a Professional CTA - Lazy loaded */}
        <Suspense fallback={<SectionSkeleton height="h-64" />}>
          <BecomeProCTA />
        </Suspense>
      </main>

      <Footer />
    </div>
  );
};

export default Index;
